//@flow
import React from 'react';
import { MarkerType } from 'common/enums/MapEnum';
import TransitionGroupMarker from 'common/components/core/markers/TransitionGroupMarker';
import LatLngUtils from 'common/utils/LatLngUtils';
import mapValues from 'lodash/mapValues';
import uniqWith from 'lodash/uniqWith';
import { dashboardColors } from 'common/enums/ColorEnum';

const FpPlanMapWithSpideringFactory = Map => {
    class FpPlanMapWithSpidering extends React.Component {
        getMarkerForVisit = (child, isUserVisit) => {
            return (
                <TransitionGroupMarker
                    {...child.props}
                    key={child.props.id}
                    lat={child.props.lat}
                    lng={child.props.lng}
                    markerType={
                        isUserVisit
                            ? MarkerType.NON_HOVERABLE
                            : MarkerType.HOVERABLE
                    }
                >
                    {child}
                </TransitionGroupMarker>
            );
        };

        getSpiderMarkerIcon = pointsList => {
            const { defaultMarkerIcon } = this.props;
            const isAnyOverlappingVisitHomebase = pointsList.some(point => {
                const { visit } = point.child.props;
                return visit?.isHomebase?.();
            });
            if (isAnyOverlappingVisitHomebase) {
                return 'homebase';
            }
            return defaultMarkerIcon;
        };

        overrideClusterColor = points => {
            const areAllMarkersInSpiderOfSamePool = points.every(
                (point, index, points) => {
                    return (
                        point.child.props.color === points[0].child.props.color
                    );
                }
            );
            if (areAllMarkersInSpiderOfSamePool) {
                return points[0].child.props.color;
            } else {
                //check if an unallocated marker is present
                const isUnallocatedMarkerPresent = points.some(
                    point => point.child.props.isUnallocated
                );
                if (isUnallocatedMarkerPresent) {
                    return dashboardColors.salmonTwo;
                }
                return dashboardColors.slate;
            }
        };

        getCustomSpiderKey(latLngsObject) {
            const hash = LatLngUtils.createGeohash(
                latLngsObject.lat,
                latLngsObject.lng
            );
            return hash;
        }

        areAllPointsTheSame = points =>
            uniqWith(points, (point1, point2) => {
                return point1.lat === point2.lat && point1.lng === point2.lng;
            }).length === 1;

        roundLatLng = point =>
            mapValues(
                point,
                coordinate => Math.round(coordinate * 10000) / 10000
            );

        // Aggregating markers that are within a 10m range by rounding off
        // if all markers are not pointing to the same location.
        getLatLngToPointsMap = points => {
            const latLngToPointsMap = {};
            const areAllPointsTheSame = this.areAllPointsTheSame(points);

            points.forEach(point => {
                if (
                    !point.child.props.isArchived &&
                    !point.child.props.doNotSpider
                ) {
                    const { lat, lng } = point;
                    let latLng = { lat, lng };
                    latLng = !areAllPointsTheSame
                        ? this.roundLatLng(latLng)
                        : latLng;

                    const latlngString = JSON.stringify(latLng);
                    if (latLngToPointsMap[latlngString]) {
                        latLngToPointsMap[latlngString].push(point);
                    } else {
                        latLngToPointsMap[latlngString] = [point];
                    }
                }
            });
            return latLngToPointsMap;
        };

        overrideMarkerSpidering = pointsList => {
            if (pointsList) {
                if (pointsList.length === 1) {
                    return pointsList[0].child;
                } else {
                    //check if one task is combined with user visits
                    const isOnlyOneTaskVisitPresent =
                        pointsList.filter(point => {
                            return point.child.props.visit?.outletVisitId;
                        }).length === 1;

                    if (isOnlyOneTaskVisitPresent) {
                        return this.getMarkersWithoutSpidering(pointsList);
                    }
                }
            }
        };

        getMarkersWithoutSpidering = pointsList => {
            const userVisitMarkers = [];
            const taskVisitMarker = [];
            // Pushing the taskVisit at the end so its visible on top of others
            // and making the user visit non hoverable from map
            pointsList.forEach(point => {
                if (point.child.props.visit?.outletVisitId) {
                    taskVisitMarker.push(
                        this.getMarkerForVisit(point.child, false)
                    );
                } else {
                    userVisitMarkers.push(
                        this.getMarkerForVisit(point.child, true)
                    );
                }
            });
            return [...userVisitMarkers, ...taskVisitMarker];
        };

        render() {
            const { innerRef, ...others } = this.props;
            return (
                <Map
                    {...others}
                    ref={innerRef}
                    disableVirtualization
                    disableSpiderMarkerDrag
                    disableHidePopupIfChildSelected
                    getCustomSpiderKey={this.getCustomSpiderKey}
                    getLatLngToPointsMap={this.getLatLngToPointsMap}
                    getSpiderMarkerIcon={this.getSpiderMarkerIcon}
                    overrideMarkerSpidering={this.overrideMarkerSpidering}
                    overrideClusterColor={this.overrideClusterColor}
                    positionControlsToLeft
                />
            );
        }
    }
    return FpPlanMapWithSpidering;
};

export { FpPlanMapWithSpideringFactory };
